import {
  GeofenceSiteGroupEntity,
  siteApi,
  SiteEntity,
  SitePatchDTO,
  sitesSelector,
  useGetAllSitesByGeofenceSiteGroupQuery
} from "@ots-frontend/ots-data";
import {ActionIcon, Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {SiteDataTable} from "../../data-table/SiteDataTable/SiteDataTable";
import {IconPlus} from "@tabler/icons-react";
import {useCallback, useMemo} from "react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {useDisclosure} from "@mantine/hooks";
import {SiteSelectModal} from "../../select/entity/SiteSelect/SiteSelect";
import {useSiteMutation} from "../../../hooks/useSiteMutation";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";

export type GeofenceSiteGroupDetailsProps = {
  geofenceSiteGroup: GeofenceSiteGroupEntity
}

export const GeofenceSiteGroupDetails = (props: GeofenceSiteGroupDetailsProps) => {
  const {geofenceSiteGroup} = props;

  const {regionId} = useApp()
  const {t} = useTranslation(["common"]);

  const siteModalDisclosure = useDisclosure(false);
  const siteModalOpened = siteModalDisclosure[0]
  const siteModalHandlers = siteModalDisclosure[1]

  const selectSites = useMemo(() => {
    return sitesSelector();
  }, []);

  const {sites, isSitesLoading, refetch} = useGetAllSitesByGeofenceSiteGroupQuery(regionId ? {
    regionId: regionId,
    geofenceSiteGroupId: geofenceSiteGroup.id,
  } : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      sites: selectSites(result, {}),
      isSitesLoading: result.isLoading
    })
  });

  const {
    patchUpdateSite,
  } = useSiteMutation()

  const onAddSite = useCallback((sites: SiteEntity[] | undefined) => {
      if (sites) {
        for (const site of sites) {
          if (geofenceSiteGroup.sites.findIndex(s => s.id === site.id) === -1) {
            const patch: SitePatchDTO = {
              geofenceSiteGroupId: {
                present: true,
                value: geofenceSiteGroup.id,
              }
            }

            patchUpdateSite(site.id, patch, {
              onSuccess: () => {
                refetch()
              }
            })
          }
        }
      }
      siteModalHandlers.close()
    },
    [geofenceSiteGroup, patchUpdateSite, refetch, siteModalHandlers]
  );

  return (
    <Grid>
      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{
              t("common.entity.geofencesitegroup.profile.info.section.details.label", "Details")}
            </Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.geofencesitegroup.profile.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {geofenceSiteGroup.name}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>
      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title order={4}>
              {t("common.entity.site.plural", "Sites")}
            </Title>
            <Group spacing={4} position="left" noWrap>
              <HasAccess permissions={["site_edit"]}>
                <ActionIcon color="green" onClick={siteModalHandlers.open}>
                  <IconPlus size={16}/>
                </ActionIcon>
              </HasAccess>
            </Group>
          </Group>
          <Divider pb="sm"/>
          <SiteDataTable
            withBorder
            records={sites}
            fetching={isSitesLoading}
          />
        </Card>
      </Grid.Col>

      <SiteSelectModal
        select={{
          withinPortal: true,
          selector: {
            regionId: regionId,
          }
        }}
        title={t<string>("common.entity.site", "Site")}
        size="lg"
        opened={siteModalOpened}
        onClose={siteModalHandlers.close}
        onSubmit={onAddSite}
      />

    </Grid>
  );
};
